import { useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { isPerfilDiretivo } from '../lib/accessControl'
import { countAprovacoesPendentesVisiveis, isAprovacaoVisivelParaUsuario } from '../lib/aprovacaoVisibility'
import { persistAppendOnlyVersion, persistDeletedVersion } from '../lib/lumiAppendOnly'
import { formatDate } from '../utils'
import type { Aprovacao } from '../types'

const ETAPAS: Aprovacao['etapa_aprovacao'][] = ['ENGENHARIA', 'SUPRIMENTOS', 'FINANCEIRO', 'DIRETORIA', 'ADMINISTRADOR']

const ETAPA_LABEL: Record<string, string> = {
  ENGENHARIA: 'Engenharia',
  SUPRIMENTOS: 'Suprimentos',
  FINANCEIRO: 'Financeiro',
  DIRETORIA: 'Diretoria',
  ADMINISTRADOR: 'Administrador',
}

const ENTIDADE_LABEL: Record<string, string> = {
  REQUISICAO: 'Requisição',
  COTACAO: 'Cotação',
  ORDEM_COMPRA: 'Ordem de Compra',
  CONTA_PAGAR: 'Conta a Pagar',
  PAGAMENTO: 'Pagamento',
  ALTERACAO_ORCAMENTO: 'Alteracao de Orcamento',
}

export default function Aprovacoes() {
  const {
    aprovacoes, setAprovacoes, currentUser, requisicoes, setRequisicoes, ordensCompra, setOrdensCompra,
    cotacoes, setCotacoes, contasPagar, setContasPagar, pagamentos, itensOrcados, setItensOrcados, obras, addLog,
  } = useApp()
  const [filtroStatus, setFiltroStatus] = useState<'PENDENTE' | 'APROVADO' | 'REJEITADO' | ''>('')
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroEtapa, setFiltroEtapa] = useState('')
  const [filtroObra, setFiltroObra] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showModal, setShowModal] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [observacao, setObservacao] = useState('')
  const [decisao, setDecisao] = useState<'APROVADO' | 'REJEITADO'>('APROVADO')
  const [showLimparModal, setShowLimparModal] = useState(false)
  const [limpando, setLimpando] = useState(false)

  function getObraId(a: Aprovacao) {
    if (a.entidade_tipo === 'REQUISICAO') return requisicoes.find(r => r.id === a.entidade_id)?.obra_id
    if (a.entidade_tipo === 'COTACAO') {
      const cotacao = cotacoes.find(c => c.id === a.entidade_id)
      return requisicoes.find(r => r.id === cotacao?.requisicao_id)?.obra_id
    }
    if (a.entidade_tipo === 'ORDEM_COMPRA') return ordensCompra.find(o => o.id === a.entidade_id)?.obra_id
    if (a.entidade_tipo === 'CONTA_PAGAR') return contasPagar.find(c => c.id === a.entidade_id)?.obra_id
    if (a.entidade_tipo === 'PAGAMENTO') {
      const pagamento = pagamentos.find(p => p.id === a.entidade_id)
      return contasPagar.find(c => c.id === pagamento?.conta_pagar_id)?.obra_id
    }
    if (a.entidade_tipo === 'ALTERACAO_ORCAMENTO') return itensOrcados.find(item => item.id === a.entidade_id)?.obra_id
    return undefined
  }

  function getObraLabel(a: Aprovacao) {
    const obraId = getObraId(a)
    return obras.find(obra => obra.id === obraId)?.nome_obra || '-'
  }

  const lista = aprovacoes.filter(a => {
    if (!isAprovacaoVisivelParaUsuario(a, currentUser)) return false
    if (filtroStatus && a.status !== filtroStatus) return false
    if (filtroTipo && a.entidade_tipo !== filtroTipo) return false
    if (filtroEtapa && a.etapa_aprovacao !== filtroEtapa) return false
    if (filtroObra && getObraId(a) !== filtroObra) return false
    return true
  })
  const paginated = paginateItems(lista, page, pageSize)

  const pendentes = countAprovacoesPendentesVisiveis(aprovacoes, currentUser)

  function getEntidadeLabel(a: Aprovacao) {
    if (a.entidade_tipo === 'REQUISICAO') {
      const r = requisicoes.find(r => r.id === a.entidade_id)
      return r ? `${r.numero_requisicao} — ${r.solicitante_nome}` : a.entidade_id
    }
    if (a.entidade_tipo === 'ORDEM_COMPRA') {
      const o = ordensCompra.find(o => o.id === a.entidade_id)
      return o ? o.numero_oc : a.entidade_id
    }
    if (a.entidade_tipo === 'COTACAO') {
      const c = cotacoes.find(c => c.id === a.entidade_id)
      return c ? c.numero_cotacao : a.entidade_id
    }
    if (a.entidade_tipo === 'CONTA_PAGAR') {
      const c = contasPagar.find(c => c.id === a.entidade_id)
      return c ? c.numero_titulo : a.entidade_id
    }
    if (a.entidade_tipo === 'ALTERACAO_ORCAMENTO') {
      const item = itensOrcados.find(item => item.id === a.entidade_id)
      return item ? `${item.codigo_item} - ${item.nome_item}` : a.entidade_id
    }
    return a.entidade_id
  }

  function openDecisao(id: string, tipo: 'APROVADO' | 'REJEITADO') {
    setSelectedId(id)
    setDecisao(tipo)
    setObservacao('')
    setShowModal(true)
  }

  async function apagarHistorico() {
    if (!isPerfilDiretivo(currentUser?.perfil)) {
      setShowLimparModal(false)
      return
    }
    setLimpando(true)
    const total = aprovacoes.length
    try {
      for (const ap of aprovacoes) {
        await persistDeletedVersion('aprovacoes', ap as unknown as Record<string, unknown>)
      }
      setAprovacoes([])
      addLog('APROVACAO', 'HISTORICO', 'CANCELADO', `Histórico de aprovações apagado por ${currentUser?.nome} (${total} registro${total !== 1 ? 's' : ''}).`)
    } catch (error) {
      console.error('Falha ao apagar histórico de aprovações no Lumi:', error)
    } finally {
      setLimpando(false)
      setShowLimparModal(false)
    }
  }

  async function confirmarDecisao() {
    if (!selectedId) return
    const ap = aprovacoes.find(a => a.id === selectedId)
    if (!ap) return
    if (!isPerfilDiretivo(currentUser?.perfil)) {
      setShowModal(false)
      return
    }
    const decisaoAtualizada = {
      ...ap,
      status: decisao,
      aprovador_nome: currentUser?.nome || 'Usuario',
      perfil_aprovador: currentUser?.perfil || '',
      data_decisao: new Date().toISOString(),
      observacao: observacao || ap.observacao,
      liberacao_utilizada: ap.entidade_tipo === 'ALTERACAO_ORCAMENTO' && decisao === 'APROVADO' ? true : ap.liberacao_utilizada,
    } as Aprovacao

    const confirmarAprovacaoNoLumi = async () => {
      await persistAppendOnlyVersion('aprovacoes', decisaoAtualizada as unknown as Record<string, unknown>)
      setAprovacoes(prev => prev.map(a => a.id === selectedId ? decisaoAtualizada : a))
    }

    if (ap.entidade_tipo !== 'ALTERACAO_ORCAMENTO') {
      try {
        await confirmarAprovacaoNoLumi()
      } catch (error) {
        console.error('Falha ao confirmar decisao no Lumi:', error)
        return
      }
    }
    setAprovacoes(prev => prev.map(a => a.id === selectedId ? {
      ...a,
      status: decisao,
      aprovador_nome: currentUser?.nome || 'Usuário',
      perfil_aprovador: currentUser?.perfil || '',
      data_decisao: new Date().toISOString(),
      observacao: observacao || a.observacao,
      liberacao_utilizada: a.entidade_tipo === 'ALTERACAO_ORCAMENTO' && decisao === 'APROVADO' ? true : a.liberacao_utilizada,
    } : a))

    if (ap.entidade_tipo === 'REQUISICAO') {
      setRequisicoes(prev => prev.map(r => r.id === ap.entidade_id ? { ...r, status: decisao === 'APROVADO' ? 'APROVADA' : 'REJEITADA' } : r))
    }
    if (ap.entidade_tipo === 'COTACAO') {
      setCotacoes(prev => prev.map(c => c.id === ap.entidade_id ? { ...c, status: decisao === 'APROVADO' ? 'CONCLUIDA' : 'CANCELADA' } : c))
    }
    if (ap.entidade_tipo === 'ORDEM_COMPRA') {
      setOrdensCompra(prev => prev.map(o => o.id === ap.entidade_id ? { ...o, status: decisao === 'APROVADO' ? 'APROVADA' : 'CANCELADA' } : o))
    }
    if (ap.entidade_tipo === 'CONTA_PAGAR') {
      setContasPagar(prev => prev.map(c => c.id === ap.entidade_id ? { ...c, status: decisao === 'APROVADO' ? 'PENDENTE' : 'CANCELADO' } : c))
    }
    if (ap.entidade_tipo === 'ALTERACAO_ORCAMENTO') {
      const item = itensOrcados.find(i => i.id === ap.entidade_id)

      if (decisao === 'APROVADO' && item) {
        if (ap.tipo_alteracao_orcamento === 'EXCLUSAO') {
          try {
            await persistDeletedVersion('itens_orcados', item as unknown as Record<string, unknown>)
          } catch (error) {
            console.error('Falha ao confirmar exclusao do item orcado no Lumi:', error)
            setAprovacoes(prev => prev.map(a => a.id === selectedId ? ap : a))
            return
          }
          setItensOrcados(prev => prev.filter(i => i.id !== ap.entidade_id))
          addLog('ITEM_ORCADO', ap.entidade_id, 'CANCELADO', `Exclusao de item orcado aprovada por ${currentUser?.nome}. ${observacao}`)
        } else if (ap.dados_alteracao_orcamento) {
          const itemAtualizado = {
            ...item,
            ...ap.dados_alteracao_orcamento,
            saldo_atual: ap.dados_alteracao_orcamento?.saldo_atual ?? item.saldo_atual,
          } as typeof item
          try {
            await persistAppendOnlyVersion('itens_orcados', itemAtualizado as unknown as Record<string, unknown>)
          } catch (error) {
            console.error('Falha ao confirmar alteracao do item orcado no Lumi:', error)
            setAprovacoes(prev => prev.map(a => a.id === selectedId ? ap : a))
            return
          }
          setItensOrcados(prev => prev.map(i => i.id === ap.entidade_id ? {
            ...itemAtualizado,
          } as typeof i : i))
          addLog('ITEM_ORCADO', ap.entidade_id, 'EDITADO', `Alteracao de item orcado aprovada e aplicada por ${currentUser?.nome}. ${observacao}`)
        }
      }

      if (decisao === 'REJEITADO') {
        addLog('ITEM_ORCADO', ap.entidade_id, 'REJEITADO', `Alteracao de item orcado rejeitada por ${currentUser?.nome}. ${observacao}`)
      }

      try {
        await confirmarAprovacaoNoLumi()
      } catch (error) {
        console.error('Falha ao confirmar decisao no Lumi:', error)
        setAprovacoes(prev => prev.map(a => a.id === selectedId ? ap : a))
        return
      }
    }
    addLog(ap.entidade_tipo, ap.entidade_id, decisao === 'APROVADO' ? 'APROVADO' : 'REJEITADO', `${ETAPA_LABEL[ap.etapa_aprovacao]} ${decisao === 'APROVADO' ? 'aprovado' : 'rejeitado'} por ${currentUser?.nome}. ${observacao}`)
    setShowModal(false)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Aprovações</h1>
          <p className="text-xs text-gray-500 mt-0.5">Fila de aprovações e trilha de decisões</p>
        </div>
        <div className="flex items-center gap-3">
          {pendentes > 0 && (
            <span className="bg-yellow-500/20 text-yellow-400 text-xs font-semibold px-3 py-1 rounded-full border border-yellow-500/30">
              {pendentes} pendente{pendentes > 1 ? 's' : ''}
            </span>
          )}
          {isPerfilDiretivo(currentUser?.perfil) && aprovacoes.length > 0 && (
            <button
              className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-3 py-1.5 rounded border border-red-500/30 transition-colors"
              onClick={() => setShowLimparModal(true)}
            >
              Apagar histórico
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select className="input-field w-40" value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value as any); setPage(1) }}>
          <option value="">Todos os status</option>
          <option value="PENDENTE">Pendente</option>
          <option value="APROVADO">Aprovado</option>
          <option value="REJEITADO">Rejeitado</option>
        </select>
        <select className="input-field w-44" value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value); setPage(1) }}>
          <option value="">Todos os tipos</option>
          {Object.entries(ENTIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="input-field w-44" value={filtroEtapa} onChange={e => { setFiltroEtapa(e.target.value); setPage(1) }}>
          <option value="">Todas as etapas</option>
          {ETAPAS.map(etapa => <option key={etapa} value={etapa}>{ETAPA_LABEL[etapa]}</option>)}
        </select>
        <select className="input-field min-w-[220px]" value={filtroObra} onChange={e => { setFiltroObra(e.target.value); setPage(1) }}>
          <option value="">Todas as obras</option>
          {obras.map(obra => <option key={obra.id} value={obra.id}>{obra.nome_obra}</option>)}
        </select>
      </div>

      {/* Lista */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Tipo / Entidade</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Obra</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Etapa</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Aprovador</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Status</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Data Decisão</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Observação</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-600 text-sm">
                  Nenhuma aprovação encontrada.
                </td>
              </tr>
            ) : paginated.items.map(a => (
              <tr key={a.id} className="table-row-hover border-b border-white/[0.04]">
                <td className="px-4 py-3 text-gray-400 text-xs">{getObraLabel(a)}</td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-500 bg-white/[0.04] px-2 py-0.5 rounded mr-2">
                    {ENTIDADE_LABEL[a.entidade_tipo] || a.entidade_tipo}
                  </span>
                  <span className="text-white text-xs">{getEntidadeLabel(a)}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-indigo-400 font-medium">{ETAPA_LABEL[a.etapa_aprovacao]}</span>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{a.aprovador_nome || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`status-badge ${
                    a.status === 'PENDENTE' ? 'bg-yellow-500/15 text-yellow-400' :
                    a.status === 'APROVADO' ? 'bg-emerald-500/15 text-emerald-400' :
                    'bg-red-500/15 text-red-400'
                  }`}>{a.status}</span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{a.data_decisao ? formatDate(a.data_decisao) : '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs max-w-[180px] truncate">{a.observacao || '—'}</td>
                <td className="px-4 py-3">
                  {a.status === 'PENDENTE' && isPerfilDiretivo(currentUser?.perfil) && (
                    <div className="flex gap-1">
                      <button
                        className="text-xs bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 px-2 py-1 rounded transition-colors"
                        onClick={() => openDecisao(a.id, 'APROVADO')}
                      >Aprovar</button>
                      <button
                        className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-2 py-1 rounded transition-colors"
                        onClick={() => openDecisao(a.id, 'REJEITADO')}
                      >Rejeitar</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls
          page={paginated.safePage}
          pageSize={pageSize}
          totalItems={lista.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>


      {/* Modal apagar histórico */}
      {showLimparModal && (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center z-50">
          <div className="card w-full max-w-sm p-6 mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-white">Apagar histórico de aprovações</h2>
              <button onClick={() => setShowLimparModal(false)} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Esta ação irá apagar <span className="text-white font-medium">todas as {aprovacoes.length} aprovações</span> do histórico de forma permanente. Deseja continuar?
            </p>
            <div className="flex gap-2 justify-end mt-5">
              <button className="btn-ghost" onClick={() => setShowLimparModal(false)} disabled={limpando}>Cancelar</button>
              <button
                className="px-4 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-500 transition-colors disabled:opacity-50"
                onClick={apagarHistorico}
                disabled={limpando}
              >
                {limpando ? 'Apagando...' : 'Apagar tudo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal decisão */}
      {showModal && (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center z-50">
          <div className="card w-full max-w-sm p-6 mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-white">
                {decisao === 'APROVADO' ? '✓ Confirmar Aprovação' : '✕ Confirmar Rejeição'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
            </div>
            <div>
              <label className="label-field">Observação (opcional)</label>
              <textarea
                className="input-field"
                rows={3}
                placeholder="Justificativa ou comentário..."
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button className="btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
              <button
                className={`px-4 py-1.5 rounded text-xs font-medium text-white transition-colors ${decisao === 'APROVADO' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-red-600 hover:bg-red-500'}`}
                onClick={confirmarDecisao}
              >
                {decisao === 'APROVADO' ? 'Confirmar Aprovação' : 'Confirmar Rejeição'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
